/* preview-doc.js — ED-02 inspector injection for the review editor's live preview.
   Pure string in / string+index out (no React, no DOM) so Node can test it:
   `node --test frontend/src/lib/preview-doc.test.mjs`.

   THE CONTRACT (plan decision #1 + requirement 4):
   - buildPreviewDoc(html) returns { doc, clean, index }. `doc` is the TRANSIENT iframe
     srcdoc: sanitized, data-sec band-stamped, and carrying the inspector CSS + script.
   - `clean` is offset-preserving (stripped spans blanked, newlines kept), so `index`
     ranges are valid positions in the EDITOR BUFFER — code-jump never misaligns.
   - Nothing here is ever saved: `doc` lives only in the iframe; the save route
     hard-refuses goose-inspector markers as belt-and-braces (review-store.mjs).

   SANDBOX POSTURE: the iframe flips to sandbox="allow-scripts" (NO allow-same-origin)
   — the inspector script runs, but the previewed email stays on an opaque origin and
   cannot reach the parent DOM. All interaction crosses via postMessage, namespaced
   `goose:true`, parent verifies event.source.
*/
import { injectSections, buildElementIndex } from './source-map.js';

export const INSPECTOR_CSS_ID = 'goose-inspector-css';
export const INSPECTOR_JS_ID = 'goose-inspector-js';

/* Offset-preserving sanitizer: anything removed is replaced by spaces (newlines kept),
   so char offsets into `clean` == char offsets into the caller's buffer. Email HTML
   never legitimately carries scripts/handlers/embeds (the server-side injection_scan
   hard-fails them) — this is the belt for the preview copy, never a persistence gate. */
const blankPreservingNewlines = (m) => m.replace(/[^\n]/g, ' ');

export function sanitizeForPreview(html) {
  let out = String(html ?? '');
  out = out.replace(/<script\b[^>]*>[\s\S]*?<\/\s*script\s*>/gi, blankPreservingNewlines);
  out = out.replace(/<script\b[^>]*\/?>/gi, blankPreservingNewlines);                     // orphan/self-closing
  out = out.replace(/<\s*(iframe|object|embed|applet|frame)\b[\s\S]*?(<\/\s*\1\s*>|\/?>)/gi, blankPreservingNewlines);
  out = out.replace(/\s(on\w+)\s*=\s*"[^"]*"/gi, blankPreservingNewlines);                // inline handlers, dquoted
  out = out.replace(/\s(on\w+)\s*=\s*'[^']*'/gi, blankPreservingNewlines);                // squoted
  out = out.replace(/\s(on\w+)\s*=\s*[^\s>]+/gi, blankPreservingNewlines);                // unquoted
  out = out.replace(/((?:href|src|action|background|formaction)\s*=\s*["']?)\s*javascript:[^"'>\s]*/gi, (m, p1) => p1 + '#' + ' '.repeat(m.length - p1.length - 1));   // dead anchor, padded to preserve source offsets
  out = out.replace(/<meta\b[^>]*http-equiv\s*=\s*["']?\s*refresh\b[^>]*>/gi, blankPreservingNewlines);   // RW-05 (🐓, 🌑/🧊 OBS): a meta refresh navigates the frame
  out = out.replace(/<base\b[^>]*>/gi, blankPreservingNewlines);                                                   // RW-05: <base href> would send relative asset loads to a foreign host (a <base target> goes with it — the frame suppresses link navigation, so nothing is lost)
  return out;
}

/* Inspector styles — hover (dashed) / caret-linked (thin solid) / selected (solid+fill)
   / flash pulse / numbered comment pins. Class prefix goose-i- ; ids goose-inspector-*.
   These strings are what the never-saved test greps for in buffers + on-disk artifacts. */
export const INSPECTOR_CSS = `
[data-el]{cursor:pointer;}
.goose-i-hover{outline:2px dashed rgba(60,125,217,.65) !important;outline-offset:-2px;}
.goose-i-active{outline:2px solid rgba(60,125,217,.5) !important;outline-offset:-2px;background:rgba(60,125,217,.05);}
.goose-i-sel{outline:2px solid #3C7DD9 !important;outline-offset:-2px;background:rgba(60,125,217,.10);}
.goose-i-flash{animation:goose-i-pulse .9s ease-out 2;}
@keyframes goose-i-pulse{0%{background:rgba(60,125,217,.30);}100%{background:transparent;}}
.goose-i-pin{position:absolute;z-index:2147483000;width:20px;height:20px;border-radius:10px;border:2px solid #fff;
  background:#3C7DD9;color:#fff;font:700 11px/16px Arial,sans-serif;text-align:center;padding:0;cursor:pointer;
  box-shadow:0 1px 4px rgba(0,0,0,.35);}
.goose-i-pin:hover{background:#2f66b4;}
`;

/* The inspector script. Runs INSIDE the sandboxed iframe (allow-scripts, opaque origin).
   It re-derives band membership from [data-sec] (authored or string-stamped — identical
   markup either way) and enumerates sub-elements with the SAME predicate + document
   order as buildElementIndex (source-map.js), stamping data-el="<sec>/e<NN>". The
   parent zips by ordinal and validates by count (zipElements) — a mismatch degrades to
   band-level, honestly. Never interpolate build data into this string. */
export const INSPECTOR_JS = `(function(){
  if (window.__gooseInspector) return; window.__gooseInspector = true;
  var SEL = null, ACTIVE = null, HOVER = null, PIN_KEYS = [];
  function txt(el){ return el.textContent || ''; }
  function selectable(el){
    var t = el.tagName;
    if (t === 'A' || t === 'IMG' || t === 'HR') return true;
    if (t === 'P' || t === 'LI' || t === 'H1' || t === 'H2' || t === 'H3' || t === 'H4' || t === 'H5' || t === 'H6')
      return txt(el).replace(/\\s+/g, ' ').trim().length > 0 || el.querySelector('img,a') != null;
    if (t === 'TD'){
      if (el.querySelector('table')) return false;
      return txt(el).length > 0 || el.children.length > 0;
    }
    if (t === 'DIV') return txt(el).replace(/\\s+/g, ' ').trim().length > 0;
    return false;
  }
  function enumerate(){
    var bands = Array.prototype.slice.call(document.querySelectorAll('[data-sec]'));
    var perBand = {}, total = 0;
    bands.forEach(function(b){
      var sec = b.getAttribute('data-sec');
      b.setAttribute('data-el', sec);
      var n = 0;
      (function walk(el){
        for (var i = 0; i < el.children.length; i++){
          var c = el.children[i];
          if (c.hasAttribute('data-sec')) continue;
          if (selectable(c)){ n++; c.setAttribute('data-el', sec + '/e' + (n < 10 ? '0' : '') + n); }
          walk(c);
        }
      })(b);
      perBand[sec] = n; total += n;
    });
    return { bands: bands.length, total: total, perBand: perBand };
  }
  function post(o){ o.goose = true; window.parent.postMessage(o, '*'); }
  function bandOf(el){ return el && el.closest ? el.closest('[data-region],[data-sec]') : null; }
  function bandId(b){ return b ? (b.getAttribute('data-region') || b.getAttribute('data-sec')) : null; }
  function dimension(n){ return typeof n === 'number' && isFinite(n) && n >= 0 && n <= 1e6; }
  function regionGeometry(id){
    var matches = Array.prototype.filter.call(document.querySelectorAll('[data-region],[data-sec]'), function(el){
      return el.getAttribute('data-region') === id || el.getAttribute('data-sec') === id;
    });
    if (matches.length !== 1) return null;
    try {
      var rect = matches[0].getBoundingClientRect();
      return dimension(rect.width) && dimension(rect.height) ? { width: rect.width, height: rect.height } : null;
    } catch (_) { return null; }
  }
  var sizeFrame = 0;
  function size(){
    if (sizeFrame) return;
    sizeFrame = requestAnimationFrame(function(){
      sizeFrame = 0;
      post({ type: 'size', h: document.documentElement.scrollHeight, w: document.documentElement.scrollWidth });
    });
  }
  function elOf(k){
    try { return k ? document.querySelector('[data-el="' + k + '"]') : null; } catch (_) { return null; }
  }
  function setCls(el, cls, on){ if (el) el.classList[on ? 'add' : 'remove'](cls); }
  function breadcrumb(el){
    var chain = [];
    for (var n = el; n && n !== document.body; n = n.parentElement){
      var k = n.getAttribute && n.getAttribute('data-el');
      if (k) chain.unshift(k);
    }
    return chain;
  }
  function select(k){
    setCls(elOf(SEL), 'goose-i-sel', false);
    SEL = k;
    setCls(elOf(SEL), 'goose-i-sel', true);
  }
  function clearPins(){
    Array.prototype.slice.call(document.querySelectorAll('.goose-i-pin')).forEach(function(p){ p.remove(); });
  }
  function renderPins(pins){
    clearPins(); PIN_KEYS = [];
    pins.forEach(function(p){
      var el = elOf(p.elKey); if (!el) return;
      var r = el.getBoundingClientRect();
      var d = document.createElement('button');
      d.className = 'goose-i-pin'; d.textContent = String(p.n);
      d.title = p.label || 'comment';
      d.style.left = Math.max(0, r.left + window.scrollX - 9) + 'px';
      d.style.top = Math.max(0, r.top + window.scrollY - 9) + 'px';
      d.addEventListener('click', function(ev){
        ev.preventDefault(); ev.stopPropagation();
        select(p.elKey);
        post({ type: 'select', elKey: p.elKey, breadcrumb: breadcrumb(el), pin: true });
      });
      document.body.appendChild(d);
      PIN_KEYS.push(p.elKey);
    });
  }
  var manifest = enumerate();

  document.addEventListener('mouseover', function(e){
    var t = e.target && e.target.closest ? e.target.closest('[data-el]') : null;
    var k = t ? t.getAttribute('data-el') : null;
    if (k === HOVER) return;
    setCls(elOf(HOVER), 'goose-i-hover', false);
    HOVER = k;
    if (t && k !== SEL) setCls(t, 'goose-i-hover', true);
    post({ type: 'hover', elKey: k });
  }, true);
  document.addEventListener('mouseout', function(e){
    if (!e.relatedTarget || (e.relatedTarget.closest && !e.relatedTarget.closest('[data-el="' + (HOVER || '') + '"]'))){
      setCls(elOf(HOVER), 'goose-i-hover', false); HOVER = null;
      post({ type: 'hover', elKey: null });
    }
  }, true);
  document.addEventListener('click', function(e){
    // emails carry real <a href> — a click must never navigate the preview away
    e.preventDefault(); e.stopPropagation();
    var t = e.target && e.target.closest ? e.target.closest('[data-el]') : null;
    var band = bandId(bandOf(e.target));
    if (!t){ select(null); post({ type: 'deselect', band: band }); return; }
    var k = t.getAttribute('data-el');
    select(k);
    post({ type: 'select', elKey: k, breadcrumb: breadcrumb(t), band: band });
  }, true);
  document.addEventListener('keydown', function(e){
    if (e.key === 'Escape'){ select(null); post({ type: 'deselect', band: null }); }
  });

  window.addEventListener('message', function(e){
    var d = e.data;
    if (!d || d.goose !== true || e.source !== window.parent) return;
    if (d.type === 'manifest-req'){ post(Object.assign({ type: 'manifest' }, manifest)); }
    else if (d.type === 'mark'){
      if (typeof d.cls !== 'string' || !/^[a-z][\\w-]*$/.test(d.cls)
        || !Array.isArray(d.keys) || !d.keys.every(function(k){ return typeof k === 'string'; })) return;
      document.querySelectorAll('[data-region],[data-sec]').forEach(function(b){
        b.classList.toggle(d.cls, d.keys.indexOf(bandId(b)) !== -1);
      });
      size();
    }
    else if (d.type === 'measure-req'){
      if (!dimension(d.w)) return;
      var docW = Math.max(document.documentElement.scrollWidth || 0, document.body.scrollWidth || 0);
      var over = docW - d.w, bestId = null, bestNeed = 0;
      if (over > 1){
        var probe = document.createElement('div');
        probe.setAttribute('style', 'position:absolute;left:-99999px;top:0;visibility:hidden;width:max-content;max-width:none');
        document.body.appendChild(probe);
        document.body.querySelectorAll('[data-region],[data-sec]').forEach(function(cell){
          probe.innerHTML = cell.innerHTML;
          var need = probe.getBoundingClientRect().width;
          if (need > bestNeed){ bestNeed = need; bestId = bandId(cell); }
        });
        probe.remove();
      }
      post({ type: 'overflow', docW: Math.round(docW), w: d.w, over: over > 1 ? Math.round(over) : 0,
        blocks: over > 1 && bestNeed > d.w + 1 ? [bestId] : [] });
      size();
    }
    else if (d.type === 'region-geometry-req'){
      var script = document.getElementById('goose-inspector-js');
      if (typeof d.requestId !== 'string' || !d.requestId || d.requestId.length > 160
        || typeof d.regionId !== 'string' || !d.regionId
        || typeof d.renderToken !== 'string' || !script || d.renderToken !== script.nonce) return;
      var geometry = regionGeometry(d.regionId);
      post({ type: 'region-geometry', requestId: d.requestId, regionId: d.regionId, renderToken: d.renderToken,
        measured: !!geometry, width: geometry ? geometry.width : null, height: geometry ? geometry.height : null });
    }
    else if (d.type === 'active'){
      if (typeof d.elKey !== 'string' && d.elKey !== null) return;
      setCls(elOf(ACTIVE), 'goose-i-active', false);
      ACTIVE = d.elKey;
      setCls(elOf(ACTIVE), 'goose-i-active', true);
    }
    else if (d.type === 'flash'){
      if (typeof d.elKey !== 'string') return;
      var el = elOf(d.elKey); if (!el) return;
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { el.scrollIntoView(); }
      el.classList.remove('goose-i-flash'); void el.offsetWidth; el.classList.add('goose-i-flash');
      setCls(elOf(ACTIVE), 'goose-i-active', false); ACTIVE = d.elKey; setCls(el, 'goose-i-active', true);
    }
    else if (d.type === 'pins'){
      if (!Array.isArray(d.pins) || !d.pins.every(function(p){
        return p && typeof p.elKey === 'string' && dimension(p.n) && (p.label == null || typeof p.label === 'string');
      })) return;
      renderPins(d.pins);
    }
    else if (d.type === 'deselect'){ select(null); }
  });

  window.addEventListener('resize', function(){
    if (PIN_KEYS.length) post({ type: 'pins-refresh' });
    size();
  });
  window.addEventListener('load', size);
  post(Object.assign({ type: 'manifest' }, manifest));
  size();
})();`;

/* Build the transient preview document.
   html      — the editor buffer (never mutated; strings are immutable anyway).
   Returns { doc, clean, index }:
     doc   — sanitized + band-stamped + inspector CSS/JS, for iframe srcdoc ONLY
     clean — offset-preserving sanitized buffer (what `index` was computed over)
     index — buildElementIndex(clean): elKey → buffer range + label + band */
export function buildPreviewDoc(html) {
  const clean = sanitizeForPreview(html);
  const index = buildElementIndex(clean);
  const stamped = injectSections(clean);
  const payload = `<style id="${INSPECTOR_CSS_ID}">${INSPECTOR_CSS}</style>\n<script id="${INSPECTOR_JS_ID}">${INSPECTOR_JS}</scr` + `ipt>`;
  let doc;
  if (/<\/body\s*>/i.test(stamped)) doc = stamped.replace(/<\/body\s*>/i, payload + '\n</body>');
  else doc = stamped + '\n' + payload;
  return { doc, clean, index };
}
